import {
  useCallback,
  useEffect,
  useId,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  ArrowUpRight,
  Check,
  Contrast,
  Eye,
  Fingerprint,
  LayoutList,
  LockKeyhole,
  Pin,
  Plus,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserPlus,
  Users,
  WandSparkles,
} from "lucide-react";
import { api, patch, post, remove, type Row } from "../api";
import { screens } from "../catalog";
import { displayReturnId, permissionLabel, taxText } from "../terminology";
import { Badge, Button, Callout, Empty, Modal, Skeleton } from "../ui";
import { DataTable } from "./DataTable";
import { useFormValidation, type ValidationField } from "../form-validation";
import { workspaceOperationError } from "../presentation";
import { FieldError, ValidationSummary } from "./ValidationSummary";
import "./workspace-tools.css";

type Notify = (message: string) => void;
const roleLabels: Record<string, string> = {
  workspace_admin: "Workspace administrator",
  preparer: "Preparer",
  reviewer: "Reviewer",
  viewer: "Viewer",
};
const messageOf = (
  error: unknown,
  operation = "complete this workspace action",
) => workspaceOperationError(error, operation);
const dateOf = (value: unknown) => {
  const date = new Date(String(value ?? ""));
  return Number.isNaN(date.getTime())
    ? "Not supplied"
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

export function accountValidationFields(
  id: string,
  dialog: "create" | "role",
  values: Row,
  role: string,
  mfaEnabled: boolean,
  roles: string[],
  serverErrors: Record<string, string> = {},
): ValidationField[] {
  const field = (
    key: string,
    label: string,
    rules: Partial<ValidationField> = {},
  ): ValidationField => ({
    id: `${id}-${key}`,
    label,
    value: values[key],
    ...rules,
    validate: (value) => rules.validate?.(value) || serverErrors[key],
  });
  return [
    ...(dialog === "create"
      ? [
          field("name", "Full name", { required: true, maxLength: 100 }),
          field("email", "Email address", {
            required: true,
            maxLength: 254,
            validate: (value) =>
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? "").trim())
                ? undefined
                : "Enter an email address with a name, @ sign, and domain.",
          }),
          field("initialPassword", "Initial password", {
            required: true,
            minLength: 12,
            maxLength: 256,
          }),
        ]
      : []),
    {
      id: `${id}-role`,
      label: "Workspace role",
      value: role,
      required: true,
      validate: (value) =>
        roles.includes(String(value))
          ? serverErrors.role
          : "Choose an available workspace role.",
    },
    field("currentPassword", "Your current password", {
      required: true,
      maxLength: 256,
    }),
    ...(mfaEnabled
      ? [
          field("totp", "Current authenticator code", {
            required: true,
            pattern: /^[0-9]{6}$/,
            patternMessage:
              "Enter the current six-digit code from your authenticator.",
          }),
        ]
      : []),
  ];
}

export function accountFieldFailure(
  error: unknown,
  mfaEnabled: boolean,
): { field: string; reason: string } | null {
  switch ((error as Row)?.code) {
    case "ACCOUNT_EXISTS":
      return {
        field: "email",
        reason:
          "An account already uses this email address. Use a different address or manage the existing account.",
      };
    case "INVALID_EMAIL":
      return {
        field: "email",
        reason: "Enter an email address with a name, @ sign, and domain.",
      };
    case "WEAK_PASSWORD":
      return {
        field: "initialPassword",
        reason: "Use an initial password containing 12–256 characters.",
      };
    case "INVALID_ROLE":
      return { field: "role", reason: "Choose an available workspace role." };
    case "REAUTHENTICATION_REQUIRED":
      return {
        field: "currentPassword",
        reason:
          "The current administrator password was not accepted. Enter it again.",
      };
    case "MFA_REQUIRED":
      return mfaEnabled
        ? {
            field: "totp",
            reason: "Enter the current six-digit code from your authenticator.",
          }
        : null;
    case "INVALID_MFA_CODE":
      return mfaEnabled
        ? {
            field: "totp",
            reason:
              "The verification code was not accepted or was already used. Enter a new current code.",
          }
        : null;
    default:
      return null;
  }
}

export function accountFormFailure(error: unknown, operation: string): string {
  switch ((error as Row)?.code) {
    case "INVALID_CREDENTIALS":
      return "Confirm it’s you — The administrator password or verification code was not accepted. Check the signed-in administrator’s password and use a new current authenticator code. The server did not identify which credential failed.";
    case "REAUTHENTICATION_LOCKED":
      return "Confirm it’s you — Verification is temporarily locked after repeated unsuccessful attempts. Stop retrying and wait 15 minutes before trying again.";
    case "DUPLICATE_RECORD":
      return "Account administration — This change conflicts with an existing record. Refresh the account directory and check whether the account already exists before retrying. If you retry, use a new authenticator code; an accepted code cannot be reused even when the account change fails.";
    default:
      return messageOf(error, operation);
  }
}

export function clearAccountFieldErrors(
  current: Record<string, string>,
  editedField: string,
): Record<string, string> {
  const next = { ...current };
  delete next[editedField];
  // Both credentials form one proof. A prior response must not block a fresh
  // proof locally; the server still verifies every submission and enforces MFA.
  if (editedField === "currentPassword" || editedField === "totp") {
    delete next.currentPassword;
    delete next.totp;
  }
  return next;
}

export function AdminDirectory({
  user,
  notify,
}: {
  user: Row;
  notify: Notify;
}) {
  const [members, setMembers] = useState<Row[]>([]),
    [permissions, setPermissions] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true),
    [loadError, setLoadError] = useState(""),
    [formError, setFormError] = useState("");
  const [dialog, setDialog] = useState<"create" | "role" | null>(null),
    [selected, setSelected] = useState<Row | null>(null),
    [busy, setBusy] = useState(false);
  const [role, setRole] = useState("preparer");
  const [fields, setFields] = useState<Row>({
    name: "",
    email: "",
    initialPassword: "",
    currentPassword: "",
    totp: "",
  });
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const canManage = user.role === "workspace_admin";
  const id = useId();
  const validation = useFormValidation(
    dialog === "role" ? "Change workspace role" : "Create local account",
    dialog
      ? accountValidationFields(
          id,
          dialog,
          fields,
          role,
          Boolean(user.mfaEnabled),
          (permissions?.roles || [])
            .filter((item: Row) => Object.hasOwn(roleLabels, item.id))
            .map((item: Row) => String(item.id)),
          serverErrors,
        )
      : [],
  );
  function resetFields() {
    setFields({
      name: "",
      email: "",
      initialPassword: "",
      currentPassword: "",
      totp: "",
    });
    setServerErrors({});
  }
  function editField(key: string, value: string) {
    setFields((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => clearAccountFieldErrors(current, key));
    setFormError("");
  }
  const close = useCallback(() => {
    if (!busy) {
      setDialog(null);
      setSelected(null);
      setFormError("");
      setFields({
        name: "",
        email: "",
        initialPassword: "",
        currentPassword: "",
        totp: "",
      });
      setServerErrors({});
    }
  }, [busy]);
  const load = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const [directory, roles] = await Promise.all([
        api<Row[]>("/admin/users"),
        api<Row>("/admin/permissions"),
      ]);
      if (!Array.isArray(directory) || !Array.isArray(roles.roles))
        throw new Error(
          "The directory response did not contain a valid role inventory.",
        );
      setMembers(directory);
      setPermissions(roles);
    } catch (error) {
      setLoadError(messageOf(error, "load workspace accounts and roles"));
    } finally {
      setLoading(false);
    }
  }, [canManage]);
  useEffect(() => {
    void load();
  }, [load]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canManage || busy || !dialog) return;
    if (!validation.validate()) return;
    const form = event.currentTarget,
      values = new FormData(form);
    const proof = {
      currentPassword: String(values.get("currentPassword") ?? ""),
      ...(String(values.get("totp") ?? "").trim()
        ? { totp: String(values.get("totp")).trim() }
        : {}),
    };
    setBusy(true);
    setFormError("");
    try {
      if (dialog === "create") {
        const account = await post<Row>("/admin/users", {
          name: String(values.get("name") ?? "").trim(),
          email: String(values.get("email") ?? "").trim(),
          initialPassword: String(values.get("initialPassword") ?? ""),
          role,
          ...proof,
        });
        setMembers((current) => [...current, account]);
        notify(
          "Local account created. No email invitation was sent. Share the initial password through a separate secure channel.",
        );
      } else if (selected) {
        const account = await patch<Row>(
          `/admin/users/${encodeURIComponent(String(selected.id))}`,
          { role, ...proof },
        );
        setMembers((current) =>
          current.map((member) =>
            member.id === account.id ? { ...member, ...account } : member,
          ),
        );
        notify(
          "Workspace role updated. The affected account’s existing sessions were revoked.",
        );
      }
      form.reset();
      resetFields();
      setDialog(null);
      setSelected(null);
    } catch (error) {
      const fieldFailure = accountFieldFailure(error, Boolean(user.mfaEnabled));
      if (fieldFailure)
        setServerErrors({
          [fieldFailure.field]: fieldFailure.reason,
        });
      else
        setFormError(
          accountFormFailure(
            error,
            dialog === "create"
              ? "create the local account"
              : "change the workspace role",
          ),
        );
    } finally {
      setBusy(false);
    }
  }

  if (!canManage)
    return (
      <Callout title="Administrator access required">
        The workspace directory and account-management controls are available
        only to your workspace administrator. Your account’s role is{" "}
        {roleLabels[user.role] || String(user.role)}.
      </Callout>
    );
  return (
    <section
      className="zt-workspace-tools"
      aria-label="Account and permission directory"
    >
      <div className="zt-tools-heading">
        <div>
          <span className="zt-tools-symbol">
            <Users size={20} />
          </span>
          <div>
            <h2>The people behind the work.</h2>
            <p>
              Real workspace accounts, explicit permissions, and protected
              changes.
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          disabled={loading || !permissions}
          onClick={() => {
            resetFields();
            setRole("preparer");
            setFormError("");
            setDialog("create");
          }}
        >
          <UserPlus size={15} />
          Create local account
        </Button>
      </div>
      {loadError && (
        <div className="zt-tools-error" role="alert">
          <span>{taxText(loadError)}</span>
          <Button onClick={() => void load()}>
            <RefreshCw size={14} />
            Retry
          </Button>
        </div>
      )}
      <DataTable
        records={members}
        loading={loading}
        title="Workspace accounts"
        getRowId={(record) => String(record.id)}
        emptyMessage="No accounts were returned by the directory service."
        columns={[
          {
            key: "name",
            label: "Team member",
            render: (value, record) => (
              <span className="zt-member-name">
                <span className="zt-member-avatar">
                  {String(value || "?")
                    .slice(0, 1)
                    .toUpperCase()}
                </span>
                <span>
                  {String(value)}
                  {record.id === user.id && <small>You</small>}
                </span>
              </span>
            ),
          },
          { key: "email", label: "Email" },
          {
            key: "role",
            label: "Workspace role",
            render: (value) => (
              <Badge>{roleLabels[String(value)] || String(value)}</Badge>
            ),
          },
          {
            key: "mfaEnabled",
            label: "Authenticator",
            render: (value) => (
              <span className="zt-auth-state">
                <Fingerprint size={13} />
                {value ? "Enabled" : "Not enabled"}
              </span>
            ),
          },
          { key: "createdAt", label: "Added", render: dateOf },
          {
            key: "actions",
            label: "Manage",
            sortable: false,
            render: (_, record) => (
              <Button
                disabled={record.id === user.id || !permissions}
                title={
                  record.id === user.id
                    ? "Another administrator must change your role."
                    : "Reauthentication required"
                }
                onClick={() => {
                  resetFields();
                  setSelected(record);
                  setRole(String(record.role));
                  setDialog("role");
                  setFormError("");
                }}
              >
                Change role
              </Button>
            ),
          },
        ]}
      />
      <div className="zt-admin-footnote">
        <LockKeyhole size={14} />
        <p>
          These are local application roles. They do not grant tax review,
          signing, release, or certification authority. Account creation does
          not send email.
        </p>
      </div>
      {permissions && (
        <section className="zt-permission-section">
          <div className="zt-tools-subheading">
            <div>
              <h3>Permission boundaries</h3>
              <p>Read directly from the running service.</p>
            </div>
            <Badge>Server-defined roles</Badge>
          </div>
          <div className="zt-permission-grid">
            {permissions.roles.map((item: Row) => (
              <article key={item.id}>
                <div>
                  <ShieldCheck size={16} />
                  <h4>{item.name}</h4>
                </div>
                <ul>
                  {(Array.isArray(item.permissions)
                    ? item.permissions
                    : []
                  ).map((permission: string) => (
                    <li key={permission}>
                      <Check size={12} />
                      <span>{permissionLabel(permission)}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <p className="zt-tools-caption">{permissions.scope}</p>
        </section>
      )}
      {dialog && (
        <Modal
          title={
            dialog === "create"
              ? "Create a local account"
              : `Change role · ${selected?.name || "Team member"}`
          }
          close={close}
        >
          <form
            className="zt-tools-form"
            onSubmit={submit}
            aria-busy={busy}
            noValidate
          >
            <p>
              {dialog === "create"
                ? "Create an account on this running workspace. There is no email delivery or invitation link."
                : "This change revokes the member’s active sessions. They must sign in again with their new permissions."}
            </p>
            <ValidationSummary issues={validation.issues} />
            {dialog === "create" && (
              <>
                <label htmlFor={`${id}-name`}>
                  Full name
                  <input
                    {...validation.fieldProps(`${id}-name`)}
                    name="name"
                    autoComplete="off"
                    value={fields.name}
                    onChange={(event) => editField("name", event.target.value)}
                    required
                    maxLength={100}
                    disabled={busy}
                  />
                  <FieldError issue={validation.issue(`${id}-name`)} />
                </label>
                <label htmlFor={`${id}-email`}>
                  Email address
                  <input
                    {...validation.fieldProps(`${id}-email`)}
                    name="email"
                    type="email"
                    autoComplete="off"
                    value={fields.email}
                    onChange={(event) => editField("email", event.target.value)}
                    required
                    maxLength={254}
                    disabled={busy}
                  />
                  <FieldError issue={validation.issue(`${id}-email`)} />
                </label>
                <label htmlFor={`${id}-initialPassword`}>
                  Initial password
                  <input
                    {...validation.fieldProps(`${id}-initialPassword`)}
                    name="initialPassword"
                    type="password"
                    autoComplete="section-new-account new-password"
                    value={fields.initialPassword}
                    onChange={(event) =>
                      editField("initialPassword", event.target.value)
                    }
                    required
                    minLength={12}
                    maxLength={256}
                    disabled={busy}
                  />
                  <small>
                    Password for the new account, not your administrator account.
                    At least 12 characters. Keep it out of notes and email;
                    share through a separate secure channel.
                  </small>
                  <FieldError
                    issue={validation.issue(`${id}-initialPassword`)}
                  />
                </label>
              </>
            )}
            <label htmlFor={`${id}-role`}>
              Workspace role
              <select
                {...validation.fieldProps(`${id}-role`)}
                value={role}
                onChange={(event) => {
                  setRole(event.target.value);
                  setServerErrors((current) => {
                    const next = { ...current };
                    delete next.role;
                    return next;
                  });
                  setFormError("");
                }}
                disabled={busy}
                required
              >
                {permissions?.roles
                  .filter((item: Row) => Object.hasOwn(roleLabels, item.id))
                  .map((item: Row) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>
              <FieldError issue={validation.issue(`${id}-role`)} />
            </label>
            <div className="zt-reauth">
              <div>
                <LockKeyhole size={15} />
                <strong>Confirm it’s you</strong>
              </div>
              <p>
                Verify the signed-in administrator{user.email ? `: ${user.email}` : "."}
              </p>
              <label htmlFor={`${id}-currentPassword`}>
                Your current password
                <input
                  {...validation.fieldProps(`${id}-currentPassword`)}
                  name="currentPassword"
                  type="password"
                  autoComplete="section-administrator current-password"
                  value={fields.currentPassword}
                  onChange={(event) =>
                    editField("currentPassword", event.target.value)
                  }
                  required
                  disabled={busy}
                />
                <small>
                  Use the signed-in administrator’s existing password, not the
                  new account’s password.
                </small>
                <FieldError issue={validation.issue(`${id}-currentPassword`)} />
              </label>
              {user.mfaEnabled && (
                <label htmlFor={`${id}-totp`}>
                  Current authenticator code
                  <input
                    {...validation.fieldProps(`${id}-totp`)}
                    name="totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={fields.totp}
                    onChange={(event) => editField("totp", event.target.value)}
                    required
                    disabled={busy}
                  />
                  <small>
                    Use a fresh code from this administrator’s authenticator.
                    A code accepted for sign-in or another account change cannot
                    be reused. Wait for the next code if needed.
                  </small>
                  <FieldError issue={validation.issue(`${id}-totp`)} />
                </label>
              )}
            </div>
            {formError && (
              <div className="zt-tools-error" role="alert">
                {taxText(formError)}
              </div>
            )}
            <div className="form-actions">
              <Button onClick={close} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  busy || (dialog === "role" && role === selected?.role)
                }
              >
                {busy
                  ? "Saving…"
                  : dialog === "create"
                    ? "Create account"
                    : "Update role"}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

export interface WorkspacePreferences {
  contrast: "normal" | "high";
  motion: "system" | "reduced";
  density: "comfortable" | "compact";
}
export function normalizeWorkspacePreferences(
  value: Row,
): WorkspacePreferences {
  return {
    contrast: value.contrast === "high" ? "high" : "normal",
    motion: value.motion === "reduced" ? "reduced" : "system",
    density: value.density === "compact" ? "compact" : "comfortable",
  };
}
export function applyWorkspacePreferences(value: Row) {
  const preferences = normalizeWorkspacePreferences(value);
  document.documentElement.dataset.contrast = preferences.contrast;
  document.documentElement.dataset.motion = preferences.motion;
  document.documentElement.dataset.density = preferences.density;
}
export function resolveSavedScreen(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return (
    screens.find(
      (screen) =>
        screen.path === value || screen.id === value || screen.kind === value,
    )?.path ?? null
  );
}
export interface PersonalToolsProps {
  kind: "saved-views" | "pinned" | "preferences";
  views: Row[];
  pins: Row[];
  refresh: () => Promise<void>;
  navigate: (path: string) => void;
  inspect: (record: Row) => void;
  notify: Notify;
  returnDisplayIds?: ReadonlyMap<string, string>;
  technical?: boolean;
}

export function PersonalTools({
  kind,
  views,
  pins,
  refresh,
  navigate,
  inspect,
  notify,
  returnDisplayIds,
  technical = false,
}: PersonalToolsProps) {
  const [pendingRemoval, setPendingRemoval] = useState<Row | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [openingId, setOpeningId] = useState("");
  const close = useCallback(() => {
    if (!busy) {
      setPendingRemoval(null);
      setError("");
    }
  }, [busy]);
  const isViews = kind === "saved-views";
  const savedScreenLabel = (value: unknown) =>
    screens.find((screen) => screen.path === resolveSavedScreen(value))
      ?.title ||
    (technical ? String(value || "No screen supplied") : "Screen unavailable");

  function openView(view: Row) {
    const path = resolveSavedScreen(view.filters?.screen);
    if (!path) {
      setError(
        "The saved screen is not registered in this application. No destination was guessed.",
      );
      return;
    }
    navigate(path);
    notify(
      `Opened the saved screen. Choose “${view.name}” from Views to apply its saved table settings.`,
    );
  }
  async function openPin(pin: Row) {
    setOpeningId(String(pin.id));
    setError("");
    try {
      if (pin.targetType === "catalog")
        inspect(
          await api(
            `/catalog/record?key=${encodeURIComponent(String(pin.targetId))}`,
          ),
        );
      else if (pin.targetType === "case")
        inspect(
          await api(`/cases/${encodeURIComponent(String(pin.targetId))}`),
        );
      else if (pin.targetType === "view") {
        const view = views.find((value) => value.id === pin.targetId);
        if (!view) {
          setError(
            "This saved view is no longer available. Refresh or remove its pin.",
          );
          return;
        }
        openView(view);
      } else if (pin.targetType === "page") {
        const path = resolveSavedScreen(pin.targetId);
        if (!path) {
          setError(
            "The page linked by this pin is no longer available. Remove the pin or choose an available page.",
          );
          return;
        }
        navigate(path);
      } else {
        setError(
          "This shortcut cannot be opened by the running application. Its original record has not been changed.",
        );
        return;
      }
    } catch (failure) {
      setError(messageOf(failure, "open the pinned record"));
    } finally {
      setOpeningId("");
    }
  }
  async function confirmRemoval() {
    if (!pendingRemoval || busy) return;
    setBusy(true);
    setError("");
    try {
      await remove(
        `/${isViews ? "views" : "pins"}/${encodeURIComponent(String(pendingRemoval.id))}`,
      );
      setPendingRemoval(null);
      notify(
        isViews
          ? "Saved view deleted. Source records and returns were not changed."
          : "Pin removed. The original record was not changed.",
      );
      try {
        await refresh();
      } catch {
        setError(
          `The ${isViews ? "view was deleted" : "pin was removed"}, but the latest workspace information could not be loaded. Refresh before making another change.`,
        );
      }
    } catch (failure) {
      setError(
        messageOf(
          failure,
          isViews ? "delete the saved view" : "remove the pin",
        ),
      );
    } finally {
      setBusy(false);
    }
  }
  if (kind === "preferences") return <PreferencesEditor notify={notify} />;
  const rows = isViews ? views : pins;
  return (
    <section
      className="zt-workspace-tools"
      aria-label={isViews ? "Saved views" : "Pinned records"}
    >
      <div className="zt-tools-heading">
        <div>
          <span className="zt-tools-symbol">
            {isViews ? <SlidersHorizontal size={20} /> : <Pin size={20} />}
          </span>
          <div>
            <h2>
              {isViews
                ? "Your perspective, saved."
                : "Keep the important things close."}
            </h2>
            <p>
              {isViews
                ? "Your own table settings, stored with your workspace account."
                : "Personal shortcuts to original source records and registered pages."}
            </p>
          </div>
        </div>
        <Badge>
          {rows.length} {isViews ? "views" : "pins"}
        </Badge>
      </div>
      {error && !pendingRemoval && (
        <div className="zt-tools-error" role="alert">
          <span>{taxText(error)}</span>
          <Button
            onClick={async () => {
              try {
                await refresh();
                setError("");
              } catch (failure) {
                setError(
                  messageOf(failure, "refresh personal workspace records"),
                );
              }
            }}
          >
            <RefreshCw size={14} />
            Refresh
          </Button>
        </div>
      )}
      {!rows.length ? (
        <Empty
          title={isViews ? "Make a view your own." : "Nothing pinned yet."}
          description={
            isViews
              ? "Open a source table, choose your columns and filters, then use Views to save the combination."
              : "Use the pin control on a source table to keep a record within reach."
          }
          icon={isViews ? <SlidersHorizontal size={24} /> : <Pin size={24} />}
        />
      ) : (
        <DataTable
          records={rows}
          allowExport={technical}
          getRowId={(record) => String(record.id)}
          getSearchText={(record) =>
            record.targetType === "case"
              ? displayReturnId({ id: record.targetId }, returnDisplayIds)
              : ""
          }
          title={isViews ? "My saved views" : "My pinned records"}
          columns={
            isViews
              ? [
                  {
                    key: "name",
                    label: "Saved view",
                    render: (value) => (
                      <span className="zt-personal-name">
                        <SlidersHorizontal size={15} />
                        {String(value)}
                      </span>
                    ),
                  },
                  {
                    key: "filters.screen",
                    label: "Screen",
                    getTitle: savedScreenLabel,
                    render: savedScreenLabel,
                  },
                  { key: "createdAt", label: "Saved", render: dateOf },
                  {
                    key: "actions",
                    label: "Actions",
                    sortable: false,
                    render: (_, record) => (
                      <div className="zt-row-actions">
                        <Button onClick={() => openView(record)}>
                          <ArrowUpRight size={14} />
                          Open screen
                        </Button>
                        <button
                          type="button"
                          className="zt-tool-icon"
                          aria-label={`Delete saved view ${record.name}`}
                          onClick={() => {
                            setPendingRemoval(record);
                            setError("");
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ),
                  },
                ]
              : [
                  {
                    key: "label",
                    label: "Pinned record",
                    render: (value) => (
                      <span className="zt-personal-name">
                        <Pin size={15} />
                        {String(value)}
                      </span>
                    ),
                  },
                  {
                    key: "targetType",
                    label: "Type",
                    getTitle: (value) => taxText(value),
                    render: (value) => taxText(value),
                  },
                  {
                    key: "targetId",
                    label: "Reference",
                    getTitle: (value, record) =>
                      record.targetType === "case"
                        ? displayReturnId(
                            { id: record.targetId },
                            returnDisplayIds,
                          )
                        : String(value),
                    render: (value, record) => (
                      <code>
                        {record.targetType === "case"
                          ? displayReturnId(
                              { id: record.targetId },
                              returnDisplayIds,
                            )
                          : String(value)}
                      </code>
                    ),
                  },
                  {
                    key: "actions",
                    label: "Actions",
                    sortable: false,
                    render: (_, record) => (
                      <div className="zt-row-actions">
                        <Button
                          disabled={Boolean(openingId)}
                          onClick={() => void openPin(record)}
                        >
                          <ArrowUpRight size={14} />
                          {openingId === record.id
                            ? "Opening…"
                            : record.targetType === "case"
                              ? "Open Return"
                              : "Open"}
                        </Button>
                        <button
                          type="button"
                          className="zt-tool-icon"
                          aria-label={`Remove pin ${record.label}`}
                          onClick={() => {
                            setPendingRemoval(record);
                            setError("");
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ),
                  },
                ]
          }
          onInspect={inspect}
        />
      )}
      <div className="zt-admin-footnote">
        <LockKeyhole size={14} />
        <p>
          These are personal workspace records. Removing a shortcut does not
          delete the source, modify a tax return, or change any review evidence.
        </p>
      </div>
      {pendingRemoval && (
        <Modal
          title={isViews ? "Delete this saved view?" : "Remove this pin?"}
          close={close}
        >
          <div className="zt-tools-form">
            <p>
              <strong>
                {String(pendingRemoval.name || pendingRemoval.label)}
              </strong>
            </p>
            <p>
              {isViews
                ? "Its saved columns, filters, and grouping will be deleted. You can create a new view from the source table. Original records are not affected."
                : "The shortcut will be removed from your account. Its original record remains available and unchanged."}
            </p>
            {error && (
              <div className="zt-tools-error" role="alert">
                {taxText(error)}
              </div>
            )}
            <div className="form-actions">
              <Button onClick={close} disabled={busy}>
                Keep it
              </Button>
              <Button
                variant="primary"
                onClick={() => void confirmRemoval()}
                disabled={busy}
              >
                {busy ? "Removing…" : isViews ? "Delete view" : "Remove pin"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}

function PreferencesEditor({ notify }: { notify: Notify }) {
  const [preferences, setPreferences] = useState<WorkspacePreferences>(
      normalizeWorkspacePreferences({}),
    ),
    [saved, setSaved] = useState<WorkspacePreferences>(
      normalizeWorkspacePreferences({}),
    );
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const id = useId();
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const value = normalizeWorkspacePreferences(
        await api<Row>("/preferences"),
      );
      setPreferences(value);
      setSaved(value);
      applyWorkspacePreferences(value);
    } catch (failure) {
      setError(messageOf(failure, "load your display preferences"));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const dirty = JSON.stringify(preferences) !== JSON.stringify(saved);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy || !dirty) return;
    setBusy(true);
    setError("");
    try {
      const result = normalizeWorkspacePreferences(
        await patch<Row>("/preferences", preferences),
      );
      setSaved(result);
      setPreferences(result);
      applyWorkspacePreferences(result);
      notify("Display preferences saved to your workspace account.");
    } catch (failure) {
      setError(messageOf(failure, "save your display preferences"));
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <Skeleton />;
  return (
    <section
      className="zt-workspace-tools"
      aria-label="Personal display preferences"
    >
      <div className="zt-tools-heading">
        <div>
          <span className="zt-tools-symbol">
            <Eye size={20} />
          </span>
          <div>
            <h2>A workspace that feels like yours.</h2>
            <p>
              Adjust readability, motion, and density without changing source
              records.
            </p>
          </div>
        </div>
        <Badge>Personal settings</Badge>
      </div>
      <form className="zt-preference-form" onSubmit={save} aria-busy={busy}>
        <fieldset disabled={busy}>
          <legend className="zt-sr-only">Display preferences</legend>
          <PreferenceChoice
            id={`${id}-contrast`}
            title="Contrast"
            description="Use darker labels and stronger boundaries for easier reading."
            icon={<Contrast size={20} />}
            value={preferences.contrast}
            choices={[
              {
                value: "normal",
                label: "Standard",
                detail: "Quiet tonal contrast",
              },
              {
                value: "high",
                label: "High contrast",
                detail: "Stronger text and edges",
              },
            ]}
            onChange={(contrast) =>
              setPreferences((current) => ({
                ...current,
                contrast: contrast as WorkspacePreferences["contrast"],
              }))
            }
          />
          <PreferenceChoice
            id={`${id}-motion`}
            title="Motion"
            description="System mode respects your device’s reduced-motion preference."
            icon={<WandSparkles size={20} />}
            value={preferences.motion}
            choices={[
              {
                value: "system",
                label: "Follow system",
                detail: "Use device preference",
              },
              {
                value: "reduced",
                label: "Reduced motion",
                detail: "Remove UI animation",
              },
            ]}
            onChange={(motion) =>
              setPreferences((current) => ({
                ...current,
                motion: motion as WorkspacePreferences["motion"],
              }))
            }
          />
          <PreferenceChoice
            id={`${id}-density`}
            title="Table density"
            description="Choose the amount of space around each source record."
            icon={<LayoutList size={20} />}
            value={preferences.density}
            choices={[
              {
                value: "comfortable",
                label: "Comfortable",
                detail: "More breathing room",
              },
              {
                value: "compact",
                label: "Compact",
                detail: "More rows in view",
              },
            ]}
            onChange={(density) =>
              setPreferences((current) => ({
                ...current,
                density: density as WorkspacePreferences["density"],
              }))
            }
          />
        </fieldset>
        {error && (
          <div className="zt-tools-error" role="alert">
            <span>{taxText(error)}</span>
            <Button onClick={() => void load()} disabled={busy}>
              <RefreshCw size={14} />
              Reload saved settings
            </Button>
          </div>
        )}
        <div className="zt-preference-footer">
          <span>
            {dirty
              ? "You have unsaved changes."
              : "Your current saved display settings."}
          </span>
          <div>
            <Button
              disabled={busy || !dirty}
              onClick={() => setPreferences(saved)}
            >
              Discard changes
            </Button>
            <Button variant="primary" type="submit" disabled={busy || !dirty}>
              {busy ? "Saving…" : "Save preferences"}
            </Button>
          </div>
        </div>
      </form>
      <div className="zt-admin-footnote">
        <ShieldCheck size={14} />
        <p>
          Stored by the running service for your account and workspace. No
          taxpayer data or personal settings are copied to browser local
          storage.
        </p>
      </div>
    </section>
  );
}

function PreferenceChoice({
  id,
  title,
  description,
  icon,
  value,
  choices,
  onChange,
}: {
  id: string;
  title: string;
  description: string;
  icon: ReactNode;
  value: string;
  choices: { value: string; label: string; detail: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="zt-preference-row">
      <div className="zt-preference-description">
        <span>{icon}</span>
        <div>
          <h3 id={`${id}-label`}>{title}</h3>
          <p id={`${id}-help`}>{description}</p>
        </div>
      </div>
      <div
        className="zt-preference-choices"
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-help`}
      >
        {choices.map((choice) => (
          <label
            key={choice.value}
            className={value === choice.value ? "is-selected" : ""}
          >
            <input
              type="radio"
              name={id}
              value={choice.value}
              checked={value === choice.value}
              onChange={() => onChange(choice.value)}
            />
            <span>
              <strong>{choice.label}</strong>
              <small>{choice.detail}</small>
            </span>
            <Check size={14} aria-hidden="true" />
          </label>
        ))}
      </div>
    </div>
  );
}
